// exclude-from-index
import React from 'react';
import PropTypes from 'prop-types';

import { SVGIcon } from '../../../BuildingBlocks/SVGIcon';
import { Text } from '../../../BuildingBlocks/Text';
import { Select } from '../../../BuildingBlocks/FormElements/Select';

import {
  ButtonWrapper,
  CopyWrapper,
  FlexWrapper,
  ResponsiveWrapper,
  SelectWrapper,
  StyledButton
} from './Pagination.styles';
import { colors } from '../../../css';

function Pagination(props) {
  const { page, itemTotal, perPage, prevPage, nextPage, selectOptions, showAll, selectFunc, messages } = props;
  // Not showing the pagination controls if there is not enough content to validate them being there.
  if (itemTotal === 0) return null;

  // from is 1 if perPage is undefined
  const from = perPage ? page * perPage - (perPage - 1) : 1;
  // to is itemTotal if perPage is undefined
  const to = perPage ? page * perPage : itemTotal;

  /**
   * construct options array { label, value }
   * Convert num to string, Select takes array of { string, string }
   */
  const options = selectOptions.map((item) => ({ label: item.toString(), value: item.toString() }));

  /**
   * Add "Show All" option if enabled
   */
  if (showAll) {
    options.push({
      label: messages.all,
      value: 'all'
    });
  }

  return (
    <ResponsiveWrapper>
      <FlexWrapper>
        <CopyWrapper count={itemTotal}>
          <Text.CopyParagraph tagName="div" align="center" color="tertiary" noMargins>
            {messages.rowsPerPage}
          </Text.CopyParagraph>
        </CopyWrapper>
        <SelectWrapper className="page-select">
          <Select
            id="rowsPerPage"
            name="rowsPerPage"
            label=""
            autoWidth
            onChange={selectFunc}
            value={perPage ? perPage.toString() : 'all'}
            options={options}
          />
        </SelectWrapper>
      </FlexWrapper>
      <FlexWrapper>
        <CopyWrapper count={itemTotal}>
          <Text.CopyParagraph tagName="div" align="center" color="tertiary" noMargins>
            {`${from}-${to < itemTotal ? to : itemTotal} ${messages.seperator} ${itemTotal}`}
          </Text.CopyParagraph>
        </CopyWrapper>
        {/*
         * Only render paging controls if we have more content then 1 page.
         * Undefined cannot be compared to a number, so if perPage is undefined the controls will not render,
         * Which is expected behavior
         */}
        {itemTotal > perPage && (
          <ButtonWrapper>
            <StyledButton onClick={prevPage} disabled={page <= 1}>
              <SVGIcon
                iconColor={page <= 1 ? colors.colorGrey04 : colors.colorGrey03}
                selectedIcon="arrow"
                rotate={90}
                width="14px"
                height="14px"
              />
            </StyledButton>
            <StyledButton onClick={nextPage} disabled={page >= itemTotal / perPage}>
              <SVGIcon
                iconColor={page >= itemTotal / perPage ? colors.colorGrey04 : colors.colorGrey03}
                selectedIcon="arrow"
                rotate={-90}
                width="14px"
                height="14px"
              />
            </StyledButton>
          </ButtonWrapper>
        )}
      </FlexWrapper>
    </ResponsiveWrapper>
  );
}

Pagination.propTypes = {
  /** Current Page */
  page: PropTypes.number,

  /** Total number of items in the dataset */
  itemTotal: PropTypes.number,

  /** Amount of items to display per page, passing undefined selects 'all' */
  perPage: PropTypes.number,

  /** Array of 'items per page' options. Options MUST have a different of AT LEAST 2 */
  selectOptions: PropTypes.arrayOf(PropTypes.number),

  /** Show all */
  showAll: PropTypes.bool,

  /** Optional message, meant for intl */
  messages: PropTypes.shape({
    rowsPerPage: PropTypes.string,
    seperator: PropTypes.string,
    all: PropTypes.string
  }),

  /** Function to call for next button onClick */
  prevPage: PropTypes.func.isRequired,

  /** Function to call for prev button onClick */
  nextPage: PropTypes.func.isRequired,

  /** Function to call for select onChange */
  selectFunc: PropTypes.func.isRequired
};

Pagination.defaultProps = {
  page: 1,
  itemTotal: 1,
  perPage: undefined,
  selectOptions: [9, 18, 27],
  showAll: true,
  messages: { rowsPerPage: 'Rows per page:', seperator: 'of', all: 'All' }
};

export default Pagination;
